import { hideOnScrollClasses, useHideOnScroll } from '@/hooks';
import useBannerCollapseStore from '@/hooks/Scroll/useBannerCollapseStore';
import { useGrabAndScroll } from '@/hooks/Scroll/useGrabAndScroll';
import { useScrollDetection } from '@/hooks/Scroll/useScrollDetection';
import cx from 'classnames';
import { Children, useLayoutEffect, useRef, useState } from 'react';
import { usePageBannerConfig } from './hooks/usePageBannerConfig';
import { useTitleRowLayout } from './hooks/useTitleRowLayout';
import styles from './PageBanner.module.css';
import Navigation from './Slots/Navigation';
import { Slot, SlotContentProps } from './Slots/Slot';
import Title from './Slots/Title';
import { PAGE_BANNER_TEST_IDS } from './testIds';
import { BannerSlotKeys } from './types';

interface NoteProps extends SlotContentProps {
  layout?: 'stack' | 'inline';
}

const TitleAction = ({ children }: SlotContentProps) => <Slot id={BannerSlotKeys.TitleAction}>{children}</Slot>;

const TitleMeta = ({ children }: SlotContentProps) => <Slot id={BannerSlotKeys.TitleMeta}>{children}</Slot>;

const TitleMetaPinned = ({ children }: SlotContentProps) => <Slot id={BannerSlotKeys.TitleMetaPinned}>{children}</Slot>;

const Note = ({ children, layout = 'stack' }: NoteProps) => {
  // Children.toArray drops nullish/boolean entries, so the slot stays :empty and the banner keeps its unexpanded height.
  if (Children.toArray(children).length === 0) return null;

  return (
    <Slot id={BannerSlotKeys.Note}>
      <div
        className={cx(styles.note, {
          [styles.noteStack]: layout === 'stack',
          [styles.noteInline]: layout === 'inline',
        })}
      >
        {children}
      </div>
    </Slot>
  );
};

const PageBanner = () => {
  const pageHeaderRef = useRef<HTMLDivElement>(null);
  const headerContentRef = useRef<HTMLDivElement>(null);
  const titleContainerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const titleActionRef = useRef<HTMLDivElement>(null);
  const titleMetaRef = useRef<HTMLDivElement>(null);
  const titleMetaPinnedRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isSticky, variant, hidden, dynamicHeight, isTall, syncCollapse } = usePageBannerConfig();
  const isDynamic = !!(isSticky && dynamicHeight);
  const isBannerCollapsed = useBannerCollapseStore(state => state.isCollapsed);

  const { titleFit, badgesFit } = useTitleRowLayout({
    containerRef: titleContainerRef,
    titleRef,
    titleActionRef,
    titleMetaRef,
    titleMetaPinnedRef,
  });
  const areBadgesScrollable = badgesFit === 'scroll';
  const { hasLeftScroll, hasRightScroll } = useScrollDetection(titleMetaRef);
  useGrabAndScroll(titleMetaRef, areBadgesScrollable);

  useHideOnScroll({
    targetRef: pageHeaderRef,
    hideOnScrollClass: isDynamic ? hideOnScrollClasses.dynamic : hideOnScrollClasses.offset120,
    onToggleCompact: setIsScrolled,
    overrideHidden: syncCollapse ? isBannerCollapsed : undefined,
  });

  useLayoutEffect(() => {
    const contentElement = headerContentRef.current;
    if (!contentElement) return undefined;

    const updateMeasuredHeight = () => {
      document.documentElement.style.setProperty('--ces-banner-measured-height', `${contentElement.offsetHeight}px`);
    };

    updateMeasuredHeight();
    const resizeObserver = new ResizeObserver(updateMeasuredHeight);
    resizeObserver.observe(contentElement);

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div
      ref={pageHeaderRef}
      data-banner-variant={variant}
      data-banner-scrolled={isScrolled || undefined}
      data-banner-dynamic={dynamicHeight || undefined}
      data-banner-tall={isTall || undefined}
      className={cx(styles.header, {
        [styles.sticky]: isSticky,
        [styles.hidden]: hidden,
      })}
    >
      <div ref={headerContentRef} className={styles.headerContent}>
        <div id={BannerSlotKeys.Navigation} />

        <div
          ref={titleContainerRef}
          className={styles.titleContainer}
          data-testid={PAGE_BANNER_TEST_IDS.titleContainer}
          data-title-fit={titleFit}
          data-badges-fit={badgesFit}
        >
          <div
            ref={titleRef}
            id={BannerSlotKeys.Title}
            className={styles.titleSlot}
            data-testid={PAGE_BANNER_TEST_IDS.titleSlot}
          />
          <div ref={titleActionRef} id={BannerSlotKeys.TitleAction} className={styles.titleAction} />
          <div
            ref={titleMetaRef}
            id={BannerSlotKeys.TitleMeta}
            className={styles.titleMeta}
            data-testid={PAGE_BANNER_TEST_IDS.titleMetaSlot}
            data-scroll-start={(areBadgesScrollable && hasLeftScroll) || undefined}
            data-scroll-end={(areBadgesScrollable && hasRightScroll) || undefined}
          />
          <div
            ref={titleMetaPinnedRef}
            id={BannerSlotKeys.TitleMetaPinned}
            className={styles.titleMetaPinned}
            data-testid={PAGE_BANNER_TEST_IDS.titleMetaPinnedSlot}
          />
        </div>

        <div id={BannerSlotKeys.Note} className={styles.noteContainer} />
      </div>
    </div>
  );
};

PageBanner.Title = Title;
PageBanner.TitleAction = TitleAction;
PageBanner.TitleMeta = TitleMeta;
PageBanner.TitleMetaPinned = TitleMetaPinned;
PageBanner.Navigation = Navigation;
PageBanner.Note = Note;

export default PageBanner;
